import React, { useState, useEffect } from 'react';
import {
  VerticalScreen,
  VerticalScreenHeader,
  VerticalScreenFooter,
  TitleBlock,
  RatesSection,
  RateLine,
  AvailableSpacesBlock,
} from '../components/VerticalScreen';

// Configuration initiale
const INITIAL_SPACES = 480;
const MIN_SPACES = 350;
const MAX_SPACES = 500;
const BASE_RATE_24H = 26.0;
const BASE_RATE_8DAYS = 99.0;

function Ybq() {
  const [availableSpaces, setAvailableSpaces] = useState(INITIAL_SPACES);
  const [rate24h, setRate24h] = useState(BASE_RATE_24H);
  const [rate8days, setRate8days] = useState(BASE_RATE_8DAYS);

  // Calcule le prix basé sur l'occupation
  const calculateDynamicPrice = (basePrice: number, spaces: number) => {
    const occupancyRate = 1 - spaces / MAX_SPACES;
    // Prix augmente de 0-30% selon l'occupation
    const priceMultiplier = 1 + occupancyRate * 0.3;
    const calculatedPrice = basePrice * priceMultiplier;

    // Arrondi au 0.05$ le plus proche (finit toujours par .00, .05, .10, .15, etc.)
    return Math.round(calculatedPrice * 20) / 20;
  };

  // Simule les changements d'occupation
  useEffect(() => {
    const simulateOccupancy = () => {
      setAvailableSpaces((prev) => {
        // Calcule la tendance basée sur la position actuelle
        // Si proche du minimum (350), favorise la hausse
        // Si proche du maximum (500), favorise la baisse
        const distanceFromMin = prev - MIN_SPACES;
        const distanceFromMax = MAX_SPACES - prev;

        let change;
        const random = Math.random();

        // 70% du temps: changement de 1-2 places
        // 20% du temps: changement de 3 places
        // 10% du temps: changement de 4-5 places
        let magnitude;
        if (random < 0.7) {
          magnitude = Math.floor(Math.random() * 2) + 1; // 1-2
        } else if (random < 0.9) {
          magnitude = 3;
        } else {
          magnitude = Math.floor(Math.random() * 2) + 4; // 4-5
        }

        if (distanceFromMin < 50) {
          // Proche du minimum, favorise la hausse (80% hausse, 20% baisse)
          change = Math.random() < 0.8 ? magnitude : -Math.floor(magnitude / 2);
        } else if (distanceFromMax < 50) {
          // Proche du maximum, favorise la baisse (80% baisse, 20% hausse)
          change = Math.random() < 0.8 ? -magnitude : Math.floor(magnitude / 2);
        } else {
          // Au milieu, changement équilibré (50% hausse, 50% baisse)
          change = Math.random() < 0.5 ? magnitude : -magnitude;
        }

        const newSpaces = Math.max(
          MIN_SPACES,
          Math.min(MAX_SPACES, prev + change),
        );

        // Calcule les nouveaux prix seulement si les places ont changé
        if (newSpaces !== prev) {
          const new24h = calculateDynamicPrice(BASE_RATE_24H, newSpaces);
          const new8days = calculateDynamicPrice(BASE_RATE_8DAYS, newSpaces);

          setRate24h(new24h);
          setRate8days(new8days);
        }

        return newSpaces;
      });

      // Intervalle irrégulier entre 3 et 8 secondes
      const nextInterval = Math.random() * 5000 + 3000;
      setTimeout(simulateOccupancy, nextInterval);
    };

    // Démarre la simulation après 4 secondes
    const initialTimeout = setTimeout(simulateOccupancy, 4000);

    return () => clearTimeout(initialTimeout);
  }, []);

  return (
    <VerticalScreen>
      {/* Header */}
      <VerticalScreenHeader
        logo="/images/logo/logo-ybq.png"
        title="BIENVENUE"
        backgroundColor="#0054FF"
        textColor="#fff"
      />

      {/* Main Content */}
      <div
        className="flex flex-grow flex-col justify-between px-[4vw] py-[1vh]"
        style={{ backgroundColor: '#fff', color: '#0054FF' }}
      >
        {/* Title Block - peut être répété */}
        <TitleBlock
          titleFr="TARIFS RÉGULIERS"
          titleEn="Rates"
          frColor="#0054FF"
          enColor="#009FDF"
        />

        {/* Rates Section - contient les lignes de tarifs */}
        <RatesSection borderColor="#009FDF">
          <RateLine
            text="Tarif à la barrière - 24h"
            subText="Rate at the gate - 24h"
            price={`${rate24h.toFixed(2)}$`}
            textColor="#0054FF"
            subTextColor="#009FDF"
            priceColor="#0054FF"
            borderColor="#009FDF"
          />

          <RateLine
            text="8 jours"
            subText="8 days"
            price={`99.00$`}
            textColor="#0054FF"
            subTextColor="#009FDF"
            priceColor="#0054FF"
            borderColor="#009FDF"
          />
        </RatesSection>

        {/* Bloc de titre additionnel - exemple de réutilisation */}
        <div className="mt-[1.5vh]">
          <TitleBlock
            titleFr="Taux d'occupation"
            titleEn="Occupancy rate"
            frColor="#0054FF"
            enColor="#009FDF"
          />
        </div>

        {/* Available Spaces Block */}
        <AvailableSpacesBlock
          availableSpaces={availableSpaces}
          textFr="ESPACES DISPONIBLES"
          textEn="AVAILABLES SPACES"
          numberColor="#0054FF"
          textFrColor="#0054FF"
          textEnColor="#009FDF"
        />

        {/* Payment icons section */}
        <div className="mt-[1vh] flex flex-wrap items-center justify-between">
          <div className="flex items-center gap-[1.5vw]">
            <img
              src="/images/payments/mastercard.png"
              alt="Mastercard"
              className="h-[5vh] w-[5vh] object-contain"
            />
            <img
              src="/images/payments/visa.png"
              alt="Visa"
              className="h-[5vh] w-[5vh] object-contain"
            />
            <img
              src="/images/payments/american-express.png"
              alt="American Express"
              className="h-[5vh] w-[5vh] object-contain"
            />
            <img
              src="/images/payments/interact.png"
              alt="Interact"
              className="h-[5vh] w-[5vh] object-contain"
            />
            <img
              src="/images/icons/no-cash.png"
              alt="No Cash"
              className="h-[5vh] w-[5vh] object-contain"
            />
          </div>

          <div className="flex items-center gap-[1.5vw]">
            <img
              src="/images/icons/camera.png"
              alt="Camera Surveillance"
              className="h-[5vh] w-[5vh] object-contain"
            />
            <img
              src="/images/icons/electric-icon.png"
              alt="Electric Charging"
              className="h-[5vh] w-[5vh] object-contain"
            />
          </div>
        </div>
      </div>

      {/* Thank you message */}
      {/* <div className="mb-[4vh] flex flex-col items-center">
        <p
          className="flex flex-col justify-center text-[2.6vh] font-extrabold"
          style={{ color: '#0054FF' }}
        >
          Merci de votre visite!
        </p>
        <p
          className="text-[2vh] font-bold leading-[1.2]"
          style={{ color: '#009FDF' }}
        >
          Thank you for visiting!
        </p>
      </div> */}

      {/* Footer */}
      <VerticalScreenFooter
        logo="/images/logo/logo-ybq-crop.png"
        website="www.aeroportdequebec.com"
        phone="418 555-5555"
        backgroundColor="#0054FF"
        textColor="#fff"
      />
    </VerticalScreen>
  );
}

export default Ybq;
